import { createRouter, createWebHistory } from 'vue-router'
import { GUIDED_SETUP_PATH } from '@/lib/guidedSetup'
import { grantAllowsScreen, type NavGrant } from '@/lib/navGrant'
import {
    ACCOUNT_STEP_PATH,
    leaveAccountStep,
    type OnboardingGateState,
    onboardingRedirect,
    setOnboardingGate,
} from '@/lib/onboardingGate'
import { DECIDE_PATH } from '@/lib/publicScreens'
import AccountView from '@/views/AccountView.vue'
import ActivityView from '@/views/ActivityView.vue'
import AgentsView from '@/views/AgentsView.vue'
import ApiTokensView from '@/views/ApiTokensView.vue'
import BillingView from '@/views/BillingView.vue'
import BlockersView from '@/views/BlockersView.vue'
import CatalogView from '@/views/CatalogView.vue'
import ChatView from '@/views/ChatView.vue'
import ConnectedAppsView from '@/views/ConnectedAppsView.vue'
import ConsentView from '@/views/ConsentView.vue'
import DecideView from '@/views/DecideView.vue'
import DomainsView from '@/views/DomainsView.vue'
import EnterprisePickerView from '@/views/EnterprisePickerView.vue'
import ExecutionDetailView from '@/views/ExecutionDetailView.vue'
import ExecutionsView from '@/views/ExecutionsView.vue'
import GatesView from '@/views/GatesView.vue'
import HomeView from '@/views/HomeView.vue'
import InboxView from '@/views/InboxView.vue'
import MapView from '@/views/MapView.vue'
import MemberCreateView from '@/views/MemberCreateView.vue'
import MemberDetailView from '@/views/MemberDetailView.vue'
import MemberMailView from '@/views/MemberMailView.vue'
import MembersView from '@/views/MembersView.vue'
import MemoryView from '@/views/MemoryView.vue'
import OnboardingAiAccountView from '@/views/OnboardingAiAccountView.vue'
import OnboardingGuidedView from '@/views/OnboardingGuidedView.vue'
import OnboardingMoreToolsView from '@/views/OnboardingMoreToolsView.vue'
import OnboardingTeamSetupView from '@/views/OnboardingTeamSetupView.vue'
import PlanDetailView from '@/views/PlanDetailView.vue'
import PlansView from '@/views/PlansView.vue'
import RecommendationDetailView from '@/views/RecommendationDetailView.vue'
import RoleCreateView from '@/views/RoleCreateView.vue'
import RoleDetailView from '@/views/RoleDetailView.vue'
import RolesView from '@/views/RolesView.vue'
import RunDetailView from '@/views/RunDetailView.vue'
import RunsView from '@/views/RunsView.vue'
import SelfImprovementView from '@/views/SelfImprovementView.vue'
import SpendView from '@/views/SpendView.vue'
import StaffEnterpriseDetailView from '@/views/StaffEnterpriseDetailView.vue'
import StaffEnterprisesView from '@/views/StaffEnterprisesView.vue'
import StaffExpressToolsView from '@/views/StaffExpressToolsView.vue'
import StaffPerformanceView from '@/views/StaffPerformanceView.vue'
import StaffReconciliationView from '@/views/StaffReconciliationView.vue'
import TaskCreateView from '@/views/TaskCreateView.vue'
import TaskDetailView from '@/views/TaskDetailView.vue'
import TasksView from '@/views/TasksView.vue'
import TeamCreateView from '@/views/TeamCreateView.vue'
import TeamDetailView from '@/views/TeamDetailView.vue'
import TeammateAddView from '@/views/TeammateAddView.vue'
import TeamsView from '@/views/TeamsView.vue'
import ToolsView from '@/views/ToolsView.vue'
import UsersView from '@/views/UsersView.vue'
import WaitingView from '@/views/WaitingView.vue'
import WebhookDetailView from '@/views/WebhookDetailView.vue'
import WebhooksView from '@/views/WebhooksView.vue'
import WorkItemDetailView from '@/views/WorkItemDetailView.vue'
import WorkView from '@/views/WorkView.vue'

export const router = createRouter({
    history: createWebHistory(),
    routes: [
        // The map is the way in: the enterprise's own page is one circle down from it.
        { path: '/', component: MapView },
        { path: '/enterprise', component: HomeView },
        // Which enterprise this session is for. App.vue draws it outside the shell, with `me` in hand.
        { path: '/enterprises', component: EnterprisePickerView },
        // What the signed-in person holds here and who invited them (#630): open to every grant, however narrow.
        { path: '/account', component: AccountView },
        // Onboarding's AI account step (#1716): a screen of its own, before the COO's first message, never a card in
        // the chat thread. A new owner lands here once, straight after sign-up (#1796); nobody is held here.
        { path: ACCOUNT_STEP_PATH, component: OnboardingAiAccountView },
        // Onboarding's team setup step (#1739): what the team cloned during setup still needs, one item at a time.
        { path: '/onboarding/team-setup', component: OnboardingTeamSetupView },
        { path: '/onboarding/more-tools', component: OnboardingMoreToolsView },
        // The guided setup screens (#1742): the step-by-step path, one click from the setup conversation with the COO.
        // Setup opens in that conversation; no screen asks which path to take.
        { path: GUIDED_SETUP_PATH, component: OnboardingGuidedView },
        { path: '/teams', component: TeamsView },
        { path: '/teams/new', component: TeamCreateView },
        { path: '/teams/:id', component: TeamDetailView },
        { path: '/members', component: MembersView },
        // Add a teammate (#1758): job, name, team, AI account, tools and first work in one flow, ahead of a member's page.
        { path: '/members/add', component: TeammateAddView },
        { path: '/members/new', component: MemberCreateView },
        { path: '/members/:id', component: MemberDetailView },
        // One mail of a member's mailbox on the whole page.
        { path: '/members/:id/mail/:emailId', component: MemberMailView },
        // Each teammate's work done, idle time and waiting time for a period (#2123), served under `runs`.
        { path: '/activity', component: ActivityView },
        // The humans who can reach this enterprise and what they hold (#618) — not the AI org chart above.
        { path: '/users', component: UsersView },
        { path: '/roles', component: RolesView },
        { path: '/roles/new', component: RoleCreateView },
        { path: '/roles/:id', component: RoleDetailView },
        { path: '/catalog', component: CatalogView },
        { path: '/tasks', component: TasksView },
        { path: '/tasks/new', component: TaskCreateView },
        { path: '/tasks/:id', component: TaskDetailView },
        { path: '/tasks/:taskId/recommendations/:id', component: RecommendationDetailView },
        // Chat fills the screen: the thread scrolls between its head and its composer, and the page never does.
        { path: '/chat', component: ChatView, meta: { fillsViewport: true } },
        { path: '/chat/:id', component: ChatView, meta: { fillsViewport: true } },
        // The signed-in person's own mailbox, offered in the nav once an owner has given them an address.
        { path: '/inbox', component: InboxView },
        // One mail of it on the whole page, with its conversation and ids.
        { path: '/inbox/:emailId', component: InboxView },
        // Work's merged view (#1747): every execution in one list, each opening into its runs. The four routes
        // below keep their own views, so the old Executions and Runs pages still open by URL.
        { path: '/work', component: WorkView },
        { path: '/executions', component: ExecutionsView },
        { path: '/executions/:id', component: ExecutionDetailView },
        { path: '/runs', component: RunsView },
        { path: '/runs/:id', component: RunDetailView },
        // Every plan waiting on a CEO decision — where the sidebar's Plans badge lands (#234).
        { path: '/plans', component: PlansView },
        // A plan's permanent link: shareable, and what the chat card and a run point at (#227).
        { path: '/plans/:id', component: PlanDetailView },
        { path: '/gates', component: GatesView },
        { path: '/blockers', component: BlockersView },
        // Open work nothing is working on (#1457): its own list beside Blockers and Gates, where its badge lands.
        { path: '/waiting', component: WaitingView },
        // One item of a team's work list, with its history (#1830): what a board card and a Waiting row open.
        { path: '/work-items/:id', component: WorkItemDetailView },
        // What the enterprise has learned, as a graph of its own knowledge and the evidence behind it (#690).
        { path: '/memory', component: MemoryView },
        { path: '/self-improvement', component: SelfImprovementView },
        { path: '/spend', component: SpendView },
        // The plan, this period's usage, storage and run slots against what the plan includes, payment methods,
        // invoices and the first checkout (os#1615, #1621, #1622). Every plan stop's "Upgrade" lands here.
        { path: '/billing', component: BillingView },
        { path: '/agents', component: AgentsView },
        { path: '/tools', component: ToolsView },
        { path: '/api-tokens', component: ApiTokensView },
        // The other credential that can reach this enterprise: an external agent's MCP connection (#333).
        { path: '/connected-apps', component: ConnectedAppsView },
        // The enterprise's own domain and what happens to its email there (#422).
        { path: '/domains', component: DomainsView },
        // Where an external agent asks to connect. The client_id, name and redirect come in on the query.
        { path: '/consent', component: ConsentView },
        // A Needs you email's one-time link (#1778): opens without signing in, drawn by App.vue outside the shell.
        { path: `${DECIDE_PATH}/:token`, component: DecideView },
        { path: '/webhooks', component: WebhooksView },
        { path: '/webhook/:id', component: WebhookDetailView },
        { path: '/integrations', redirect: '/tools' },
        // The Staff area's Express tools: the platform's own list, offered only to a platform admin.
        { path: '/staff/express-tools', component: StaffExpressToolsView },
        // The Staff area's enterprises (#394): every enterprise's plan, billing and usage, and one enterprise's page
        // with the actions staff take on it. Never a tenant's runs, memory, mail or secrets.
        { path: '/staff/enterprises', component: StaffEnterprisesView },
        { path: '/staff/enterprises/:id', component: StaffEnterpriseDetailView },
        // Every enterprise's recorded vs reported usage per closed period (#448's staff view).
        { path: '/staff/reconciliation', component: StaffReconciliationView },
        // Run capacity and pipeline health across every enterprise: slot use, queue wait, command time, lost work.
        { path: '/staff/performance', component: StaffPerformanceView },
    ],
})

export type { NavGrant } from '@/lib/navGrant'

/**
 * The signed-in person's grant, in the same `isOwner` / `scopes` shape `GET /auth/me` carries (#622).
 * App.vue records it as soon as `me` loads; until then nothing is known and nothing is restricted, so the
 * first navigation of a page load is not bounced out of the screen the person actually asked for.
 */
let navGrant: NavGrant | null = null

export function setNavGrant(grant: NavGrant): void {
    navGrant = grant
    // The first navigation of a page load is over before `me` answers, so a deep link into a screen this
    // person may not use has already been allowed through by then. Now that the grant is known, it is not:
    // they land on "/" rather than sitting on a view whose every request the server refuses.
    if (!grantAllowsScreen(navGrant, router.currentRoute.value.path)) {
        void router.replace('/')
    }
}

/**
 * #622: typing the address of a screen the grant does not cover does not get a person in. They land on "/"
 * — the map, their own home, which needs no scope and is therefore the one destination guaranteed to every
 * signed-in person — rather than on a blank or broken page built from calls the server refuses.
 *
 * This is the portal being honest about what it offered, not the boundary: the server refuses the data
 * whatever route the browser opened (`SessionGuard`, #617).
 */
router.beforeEach((to, from) => {
    // #1796: the new user's landing on the account step happens once. Moving off the step spends it.
    if (from.path === ACCOUNT_STEP_PATH && to.path !== ACCOUNT_STEP_PATH) {
        leaveAccountStep()
    }
    const held = onboardingHold(to.path)
    if (held) {
        return { path: held, replace: true }
    }
    if (grantAllowsScreen(navGrant, to.path)) {
        return true
    }
    return { path: '/', replace: true }
})

/**
 * #1716: where the onboarding gate sends a navigation to `path` instead, or null. Only ever a screen the grant opens,
 * so the gate and the grant check can never bounce a person between them.
 */
function onboardingHold(path: string): string | null {
    const held = onboardingRedirect(path)
    return held && grantAllowsScreen(navGrant, held) ? held : null
}

/**
 * Records the onboarding gate's state (`loadOnboardingGate`) and applies it to the screen already open: the first
 * navigation of a page load is over before the account status answers, so a new owner whose landing is due is moved
 * onto the account step now rather than left on the screen the page opened on.
 */
export function setOnboardingState(state: OnboardingGateState): void {
    setOnboardingGate(state)
    const held = onboardingHold(router.currentRoute.value.path)
    if (held) {
        void router.replace(held)
    }
}
